import {useEffect, useState} from 'react';

import {TypeLocalNameEnum} from 'domain/usecases';
import {GetExamsDistrictsParams} from 'domain/usecases/private/booking-flow/exams/get-exams-districts';

import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {useGetExamsDistrictsQuery} from '../../queries/useGetExamsDistrictsQuery';
import {UseDistrictSelectionProps} from './types';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {isUnprocessableError} from 'presentation/utils/isUnprocessableError';
import {parseCommaSeparated} from 'presentation/modules/booking/shared/utils/parseCommaSeparated';
import {textHandle} from 'presentation/utils/textHandle';

const {all, selectAll} = translations['pt-br'].commons;

export function useExamDistrictSelection({handleNextStep}: UseDistrictSelectionProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {SEARCH_PARAMS} = useQueryParams();
	const {query} = SEARCH_PARAMS;
	const bookingDistricts = parseCommaSeparated(bookingData?.districtName);
	const bookingCities = parseCommaSeparated(bookingData?.citiesNames);
	const setHideSearchBarState = useHideSearchBarStore(state => state.setHideSearchBar);
	const districtsInitialState = bookingDistricts.map((d, i) => `${d}-${bookingCities[i] ?? ''}`);
	const [selectedDistricts, setSelectedDistricts] = useState<string[]>(districtsInitialState);
	const [showEmptyDistrictsModal, setShowEmptyDistrictsModal] = useState(false);
	const {isMobile} = useMediaQueries();

	const districtParams: GetExamsDistrictsParams = {
		cdUf: bookingData.ufCode?.toUpperCase(),
		nmLocal: bookingData.cityName?.toUpperCase(),
		nmTipoLocal: bookingData.typeLocalName || TypeLocalNameEnum.CIDADE,
		cdTipoExame: bookingData.specialtyCode,
	};

	const {
		refetch,
		isError: districtListHasError,
		isFetching: districtListIsLoading,
		data: districtList,
		error: districtListError,
	} = useGetExamsDistrictsQuery(districtParams);

	const districtListHasUnprocessableError = isUnprocessableError(districtListError);

	const showNotFoundState =
		!districtListIsLoading &&
		(!districtList ||
			districtList.length === 0 ||
			(districtListHasError && districtListHasUnprocessableError));

	useEffect(() => {
		setHideSearchBarState(showNotFoundState);
	}, [setHideSearchBarState, showNotFoundState]);

	const handleSelectCard = (district: string, skipScroll?: boolean) => {
		if (district === `${all}-${selectAll}`) {
			const newAllSelected = !bookingData.allDistrictsSelected;
			const districts = districtList?.map(d => d.district);
			const cities = districtList?.map(d => d.city);

			setBookingData(prev => ({
				...prev,
				districtName: newAllSelected ? districts?.join(', ') : '',
				citiesNames: newAllSelected ? cities?.join(', ') : '',
				allDistrictsSelected: newAllSelected,
			}));

			setSelectedDistricts(
				newAllSelected ? (districtList?.map(d => `${d.district}-${d.city}`) ?? []) : [],
			);

			const shouldScrollToForwardButton =
				!isMobile && newAllSelected && districts && districts.length >= 12 && !skipScroll;

			if (shouldScrollToForwardButton) {
				document.getElementById('go-forward-button')?.scrollIntoView({behavior: 'smooth'});
			}
			return;
		}

		setSelectedDistricts(prevSelected => {
			let updatedSelected: string[];

			if (prevSelected.includes(district)) {
				updatedSelected = prevSelected.filter(item => item !== district);
			} else {
				updatedSelected = [...prevSelected, district];
			}

			const districts = updatedSelected?.map(item => item?.split('-')[0]);
			const cities = updatedSelected?.map(item => item?.split('-')[1]);

			setBookingData(prev => ({
				...prev,
				districtName: districts.join(', '),
				citiesNames: cities.join(', '),
				allDistrictsSelected: updatedSelected.length === districtList?.length,
			}));

			return updatedSelected;
		});
	};

	const handleGoForward = () => {
		if (selectedDistricts.length === 0) {
			setShowEmptyDistrictsModal(true);
			return;
		}
		handleNextStep();
	};

	const filteredDistrictList =
		districtList?.filter(districtItem =>
			textHandle(districtItem.district)
				.sanitizeText()
				.includes(textHandle(query).sanitizeText()),
		) ?? [];

	const showEmptyState = filteredDistrictList?.length === 0;

	return {
		districtListIsLoading,
		districtListHasError,
		districtListHasUnprocessableError,
		refetch,
		showNotFoundState,
		showEmptyState,
		filteredDistrictList,
		handleSelectCard,
		selectedDistricts,
		handleGoForward,
		showEmptyDistrictsModal,
		setShowEmptyDistrictsModal,
	};
}
